import $ from 'jquery'
import _ from 'lodash'
import Backbone from 'backbone'
import SharedDashboard from 'scoreboard/dashboards/shared-dashboard'
import layoutManager from 'scoreboard/components/layout-manager'
import sections from 'scoreboard/components/sections'
import appDisplay from 'scoreboard/components/app-display'
import guard from 'scoreboard/components/unsaved/guard'
import metadataObjects from './metadata-objects'
import AppDisplay from 'scoreboard/components/app-display'
import Spectator from 'scoreboard/components/spectator'

const SHARED_DASH_REGEX = /^(?:dashboards|strategy-maps|forms)\/\d*\/shared\/(\w*)$/
/**
 * The conductor provides helpers for dealing with URLs in the application.  I
 * originally wanted to use the term 'navigator' but that was taken.
 */
var conductor = {

	/**
	 * Changes the object ID portion of the given path.  For example, if the
	 * path was
	 *
	 *     scorecards/1
	 *
	 * and you passed in an ID of 2, the path would become
	 *
	 *     scorecards/2
	 */
	changeIdUrl: function(path, id) {
		if (!path || !/\d+/.test(path)) {
			return sections.default(this.section()) + '/' + id
		}
		return path.replace(/\d+/, id)
	},

	/**
	 * Changes the object ID portion of the current path.  For example, if the
	 * path was
	 *
	 *     scorecards/1
	 *
	 * and you passed in an ID of 2, the path would become
	 *
	 *     scorecards/2
	 *
	 * @param {Object} options
	 *
	 * @param {boolean} [options.force=false] force the display pane to reload
	 * even though we're already viewing the passed-in ID (#21290)
	 */
	changeId: function(id, options) {
		var fragment = Backbone.history.fragment
		fragment = this.changeIdUrl(fragment, id)
		if (_.get(options, 'force') && fragment == Backbone.history.fragment) {
			if (this.section() == 'datasets' && this.id() == id) { // #40976
				Backbone.history.loadUrl(fragment)
			} else {
				guard.conditionallyRun(() => {
					Backbone.history.loadUrl(fragment)
				})
			}
		} else {
			this.navigate(fragment, { trigger: true })
		}
	},

	/**
	 * Changes the application URL to bring up the overview screen
	 */
	overview: function(section, id) {
		this.navigate(this.overviewUrl(section, id), { trigger: true })
	},

	/**
	 * Returns the overview URL for the given section/id. Does not include
	 * the baseUrl. Example: 'scorecards/1234'
	 */
	overviewUrl: function(section, id) {
		section = section || conductor.section()
		id = id || appDisplay.domainObjectId() || conductor.id()
		return sections.toUrl(section) + '/' + id
	},

	/**
	 * Changes the application URL to bring up the edit screen
	 */
	edit: function(section, id) {
		this.navigate(this.editUrl(section, id), { trigger: true })
	},

	/**
	 * Returns the edit URL for the given section/id. Does not include
	 * the baseUrl. Example: 'scorecards/1234/edit'
	 */
	editUrl: function(section, id) {
		return this.overviewUrl(section, id) + '/edit'
	},

	/**
	 * Tests if the given URL is edit URL for the current section
	 */
	isEditUrl: function(url) {
		return url.replace(/\/$/, '').indexOf(this.editUrl()) != -1
	},

	/**
	 * Functions exactly the same as Backbone.Router.navigate
	 *
	 * @param {boolean} [options.trigger=false] if true, also call the route function
	 *
	 * @param {boolean} [options.replace=false] if true, update the URL without
	 * 	creating an entry in the browser's history
	 *
	 * @param {boolean} [options.silent=false] If true, skip Backbone.trigger('history:before-navigate')
	 * (see custom option in setup-backbone.js)
	 */
	navigate: function(route, options) {
		if (this.isSharedDashboardDrilldown(route)) {
			window.open(SharedDashboard.drilldownUrl(route))
		} else {
			this.router.navigate.apply(this.router, arguments)
		}
	},

	/**
	 * Functions the same as this.navigate(), but accounts for the fact we
	 * are drilling down, which has slightly different behavior with regard to
	 * errors.
	 *
	 * @param route
	 * @param options
	 */
	drilldown: function(route, options) {
		AppDisplay.setDrilldown()
		this.navigate(route, options)
	},

	/**
	 * Returns the path to use for evaluation paths, which may be the passed in
	 * path or a path read from Backbone or window.requestedUri
	 */
	path: function(path) {
		path = path || window.requestedUri
		// Backbone.history.fragment returns whatever is currently being
		// rendered.  This instead returns the logical URL visible at the top
		// of the browser
		path = path || Backbone.history.getPath(Backbone.history.location.pathname)
		return _.trimStart(path, '/')
	},

	/**
	 * Returns the current section.  Note the path parameter is really only for
	 * unit testing.  Normally you should just call conductor.section()
	 *
	 * example value: 'initiatives', 'strategyMaps'
	 */
	section: function(path) {
		path = this.path(path)
		if (path == null || path === '') {
			return null
		}

		return sections.fromUrl(getSectionPart(path))
	},

	/**
	 * If called with no arguments, returns the ID of the current object being viewed.
	 * If given a path, returns the ID of the object for that path.
	 */
	id: function(path) {
		path = this.path(path)
		if (path == null || path === '') {
			return null
		}

		// if the path starts with organizations, we aren't viewing an object
		if (path.indexOf('organizations') == 0) {
			return null
		}

		var matches = path.match(/\d+/)
		if (matches && matches.length > 0) {
			return Number(matches[0])
		}
		return null
	},

	// go to the login screen
	login: function() {
		window.requestedUri = Backbone.history.fragment
		// forcing a browser refresh will tell the server to render the login
		// screen for us, and will also preserve the url.  we can't render the
		// login screen ourself since only the server knows how
		location.reload(true) // pass true to ensure we don't try to hit a cache
	},

	reload: function () {
		// Note: can't use Router.navigate(), because it won't do anything if the
		// URL isn't changing
		Backbone.history.loadUrl(Backbone.history.fragment)
	},

	/**
	 * Redirects a view that is spectating another view to it's spectated view
	 * (i.e. Briefing Slide -> Scorecard Overview)
	 *
	 * @param {String} url the target Backbone route
	 * @param {String} spectated.label The label of the spectated object, to be displayed
	 * as the edit header title.
	 * @param {Number} [spectated.organizationId] The org id of the spectated domain object.
	 * If it has one this is required.  Will be set on the model used to perform
	 * security checks when determining the target route (edit or overview)
	 * @param {Boolean} [options.forceOverview=false] overrides the default behavior
	 * of the function and forces navigation to overview rather than edit. Default value is false.
	 */
	navigateToSpectated: function (url, spectated, options= {}) {
		const section = this.section(url)
		const id = this.id(url)
		var Model = metadataObjects[section].model
		var model = new Model(spectated)
		const overviewUrl = this.overviewUrl(section,id)
		let targetUrl = url
		const isEditable = _.result(model, 'isEditable')

		// Always show datasets overview, and not edit when navigating from a spectator
		// https://github.com/SpiderStrategies/Scoreboard/issues/16462#issuecomment-386595843
		const showEditView = 'datasets' !== section && isEditable && !options.forceOverview

		if (url === overviewUrl && showEditView) {
			// https://github.com/SpiderStrategies/Scoreboard/issues/16462#issuecomment-372478444
			targetUrl += '/edit'
		}
		Spectator.setCurrent(null)
		conductor.navigate(targetUrl, {trigger: true})
	},

	isSharedDashboardDrilldown(route) {
		return (layoutManager.sharedResource)
			&& !SHARED_DASH_REGEX.exec(route)
	},

	/**
	 * @param {Event} event A DOM event
	 *
	 * @returns {Boolean} if a DOM event corresponds to a user clicking on a
	 * navigation link
	 */
	isNavigationTarget(event) {
		return event && $(event.target).hasClass('sp-navigate')
	}
}

function getSectionPart (path) {
	var index = path.indexOf('/')
	if (index == -1) {
		index = path.length
	}

	var section = path.substr(0, index)
	if (section === 'organizations') {
		// e.g. 342/briefings or 342/briefings/new
		var remainingPath = path.substr(index + 1)
		// e.g. briefings or briefings/new
		remainingPath = remainingPath.substr(remainingPath.indexOf('/') + 1)
		// e.g. briefings
		if (remainingPath.indexOf('/') == -1) {
			return remainingPath
			// e.g. briefings/new
		} else {
			// strip off new
			return remainingPath.substr(0, remainingPath.indexOf('/'))
		}
	}
	return section
}

export default conductor

conductor.__filename__ = 'components/conductor.js'
